import { Component, inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterOutlet } from '@angular/router';
import { AuthService, User } from '@auth0/auth0-angular';
import { AngularPlugin, IErrorService } from '@microsoft/applicationinsights-angularplugin-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { MainNavComponent } from './core/main-nav/main-nav.component';
import { Notification, NotificationsClient, NotificationTypeEnum } from './core/services/api.service';
import { UserResolverService } from './core/services/user-resolver.service';
import { VersionCheckService } from './core/services/version-check.service';
import { SpinnerComponent } from './core/spinner/spinner.cmp';
import { TermsAndConditionsModalComponent } from './shared/modals/terms-and-conditions-modal/terms-and-conditions-modal.component';
import { WhatsNewModalComponent } from './shared/modals/whats-new-modal/whats-new-modal.component';

declare var CobrowseIO: any;

@Component({
  selector: 'status-now',
  templateUrl: `./app.component.html`,
  imports: [MainNavComponent, RouterOutlet, SpinnerComponent],
  providers: [VersionCheckService]
})

export class AppComponent implements OnInit {
  private versionCheckService = inject(VersionCheckService);
  private authService = inject(AuthService);
  private userResolverService = inject(UserResolverService);
  private notificationsClient = inject(NotificationsClient);
  private router = inject(Router);
  private dialog = inject(MatDialog);

  authenticated: boolean = false;
  isLoading: boolean = true;

  constructor() {
    const versionCheckService = this.versionCheckService;
    versionCheckService.initVersionCheck('./version.json');
  }

  ngOnInit() {
    // Subscribe to loading state first
    this.authService.isLoading$.subscribe(isLoading => {
      this.isLoading = isLoading;
    });

    // Handle authentication state changes
    this.authService.isAuthenticated$.subscribe(async (isAuthenticated) => {
      this.authenticated = isAuthenticated;

      if (isAuthenticated) {
        try {
          await this.getNotifications();
        } catch (error) {
          console.error('[AppComponent] Error getting notifications:', error);
        }

        this.authService.user$.subscribe(user => {
          if (user) {
            try {
              this.addCobrowse(user);
              this.addAppInsights(user);
            } catch (error) {
              console.error('[AppComponent] Error initializing Cobrowse/AppInsights:', error);
            }
          }
        });
      } else {
        // Handle unauthenticated state

        if (!this.isLoading) {
          try {
            await this.authService.loginWithRedirect({
              appState: {
                target: this.router.url
              }
            });
          } catch (error) {
            console.error('[AppComponent] Error initiating login redirect:', error);
          }
        }
      }
    });

    // Handle authentication errors
    this.authService.error$.subscribe(error => {
      if (error) {
        console.error('[AppComponent] Auth0 Error detected:', error);
        console.error('[AppComponent] Error type:', (error as any)?.error);
        console.error('[AppComponent] Error description:', (error as any)?.error_description);

        // Force redirect to login on authentication errors
        try {
          this.authService.loginWithRedirect({
            appState: {
              target: this.router.url
            }
          });
        } catch (redirectError) {
          console.error('[AppComponent] Error during forced login redirect:', redirectError);
        }
      }
    });
  }

  async getNotifications() {
    try {
      var termsConditions = await firstValueFrom(this.notificationsClient.notifications_GetUserTermsAndConditionNotifications());

      var notifications = await firstValueFrom(this.notificationsClient.notifications_GetUserNotifications(NotificationTypeEnum.WhatsNew));

      if (termsConditions) {
        this.showTermsConditionsNotification(termsConditions).then(() => {
          this.showNotifications(notifications);
        });
      }
      else {
        this.showNotifications(notifications);
      }
    } catch (error) {
      console.error('[AppComponent] Error in getNotifications:', error);
      throw error;
    }
  }

  showNotifications(notifications: Notification[]) {
    if (notifications?.length > 0) {
      this.dialog.open(WhatsNewModalComponent, {
        data: notifications,
        minWidth: 400,
      })
    }
  }

  showTermsConditionsNotification(termsConditions: Notification): Promise<any> {
    if (termsConditions) {
      var dialog = this.dialog.open(TermsAndConditionsModalComponent, {
        data: termsConditions,
        minWidth: 400,
        disableClose: true
      })

      return lastValueFrom(dialog.afterClosed());
    }

    return Promise.resolve();
  }

  addCobrowse(user: User) {
    try {
      CobrowseIO.license = 'ZJo7uwZW-FVb9w';
      CobrowseIO.customData = {
        email: user.email,
        device_name: user.name
      };
      CobrowseIO.start();
    } catch (error) {
      console.error('[AppComponent] Error initializing Cobrowse:', error);
    }
  }

  addAppInsights(user: User) {
    try {
      var angularPlugin = new AngularPlugin();

      const appInsights = new ApplicationInsights({
        config: {
          connectionString: environment.appInsightsConnectionString,
          extensions: [<any>angularPlugin],
          extensionConfig: {
            [angularPlugin.identifier]: {
              router: this.router,
              errorServices: [
                new CustomErrorHandler(this.userResolverService),
              ],
            },
          },
        },
      });

      appInsights.loadAppInsights();

      if (user?.email) {
        appInsights.setAuthenticatedUserContext(
          user.email,
          user.email,
          false
        );
      }
    } catch (error) {
      console.error('[AppComponent] Error initializing AppInsights:', error);
    }
  }
}

export class CustomErrorHandler implements IErrorService {
  isASMUser: boolean = false;

  constructor(userResolverService: UserResolverService) {
    userResolverService.userInfo.subscribe(x => {
      if (x.IsASM) {
        this.isASMUser = x.IsASM;
      }
    });
  }

  handleError(error: any) {
    if (error && (!environment.production || this.isASMUser)) {
      console.error('[CustomErrorHandler] Logging error:', error);
    }
  }
}
